import {
  Comment,
  commentBaseOps,
  GenericNotification,
  isReplyComment,
  isRootComment,
  NotificationSubscription,
  ReplyComment,
  RootComment,
} from '@basehub/data/replicache/index'
import { WriteTransaction } from 'replicache'
import { SetOptional, SetRequired } from 'type-fest'
import { WithServerSecret } from '../../../tx/spec'
import { hasAccessOrThrow } from '../../team-space/team/auth'
import { invariant } from '@epic-web/invariant'
import { UpdateNotificationSubscriptionData } from '../../team-space/notification/mutators'
import { getMentionsFromJson } from '@basehub/tiptap-api-utils/mentions/utils/index'
import { isServerAuthorized } from '../../../tx/server-auth'
import {
  blockBaseOps,
  ensureBaseHubTransaction,
} from '@basehub/data/replicache/tx/iso'
import { getPlaygroundUserId } from '../../../playground/ids'

// Mutators
export const addComment = async (
  tx: WriteTransaction,
  {
    authorUsername,
    comment,
    repoSlug,
    branchSlug,
    teamSlug,
    blockId,
    blockParentId,
    isDefaultBranch,
    emitNotification = true,
  }: WithServerSecret<{
    comment: Comment
    authorUsername: string
    repoSlug: string
    teamSlug: string
    branchSlug: string
    blockId: string | null | undefined
    blockParentId: string | null | undefined
    isDefaultBranch: boolean
    repoName: string
    emitNotification?: boolean
  }>
): Promise<
  { comment: Comment } & Omit<
    UpdateNotificationSubscriptionData,
    'notificationSubscription'
  > & {
      notificationSubscription:
        | null
        | SetOptional<NotificationSubscription, 'id' | 'deletedAt'>[]
    }
> => {
  ensureBaseHubTransaction(tx)
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'editor', 'member'],
    guestAccess: 'all',
  })
  if (!isServerAuthorized(tx.serverSecret)) {
    const playgroundId =
      tx.serverOnly?.playgroundId ?? tx.clientOnly?.playgroundId

    invariant(
      comment.authorId === tx.serverOnly?.session?.userId ||
        comment.authorId === tx.clientOnly?.userId ||
        (playgroundId &&
          comment.authorId === getPlaygroundUserId(playgroundId)),
      `Unauthorized`
    )
  }

  const ops: Promise<void>[] = []
  let newComment: Comment
  if (comment.type === 'root') {
    newComment = {
      ...comment,
      type: 'root',
    }
  } else {
    newComment = {
      ...comment,
      type: 'reply',
    }
  }

  const existingComment = await commentBaseOps.get(tx, newComment.id)
  if (existingComment) {
    return {
      comment,
      notificationToSend: null,
      notificationSubscription: null,
    }
  }

  let rootComment: RootComment | null =
    newComment.type === 'root' ? newComment : null
  if (isReplyComment(newComment)) {
    const replyComment = newComment as ReplyComment
    const comments = await commentBaseOps.list(tx)
    rootComment = comments.find(
      (c) => c.id === replyComment.rootId
    ) as RootComment
    invariant(rootComment, 'Root comment not found')
    invariant(isRootComment(rootComment), 'Root comment not found')

    // look for the last comment, i'm searching this because passing the parentId in client give me issues
    const replies = []
    let lastComment: Comment = rootComment
    while (lastComment) {
      const reply = comments.find((c) => c.id === lastComment.replyId)
      if (!reply) {
        break
      }
      replies.push(reply)
      lastComment = reply
    }

    if (rootComment.status === 'resolved') {
      // reopen thread
      ops.push(openComment(tx, { id: rootComment.id }))
    }
    ops.push(
      commentBaseOps.update(tx, {
        ...lastComment,
        replyId: newComment.id,
      })
    )
    newComment = {
      ...newComment,
      parentId: lastComment.id,
      rootId: rootComment.id,
      reactions: {},
    } satisfies ReplyComment
  }

  invariant(rootComment, 'Root should be defined at this point')
  let href: string = ''
  let commentRef: string = ''

  if (blockParentId) {
    commentRef = `${blockParentId}#${blockId}`
  } else if (blockId) {
    commentRef = `${blockId}`
  } else {
    commentRef = ''
  }

  const [encodedTeamSlug, encodedRepoSlug, encodedBranch, blockRef] = [
    encodeURIComponent(teamSlug),
    encodeURIComponent(repoSlug),
    encodeURIComponent(branchSlug),
    commentRef,
  ]

  const baseUrl = `/${encodedTeamSlug}/${encodedRepoSlug}`
  switch (rootComment?.ref) {
    case 'block': {
      href = `${baseUrl}/explore/${encodedBranch}/${blockRef}`
      break
    }
    case 'diff': {
      href = `${baseUrl}/changes/${encodedBranch}/${
        blockRef ? `diff:${blockRef}` : ''
      }`
      break
    }
    case 'rich-text': {
      href = `${baseUrl}/explore/${encodedBranch}/${blockRef}`
      break
    }
    default: {
      href = `${baseUrl}/explore/${encodedBranch}`
      break
    }
  }

  const url = new URL(href, 'https://basehub.com/') // we do not use the origin, so its ok to hardcode it
  url.searchParams.set('commentId', newComment.id)
  href = `${url.pathname}${url.search}${url.hash}`

  const taggedUsers = await Promise.all(
    getMentionsFromJson(comment.value).map(async (id) =>
      (await blockBaseOps.get(tx, id))?.type === 'agent' ? `agent:${id}` : id
    )
  )

  await Promise.all([...ops, commentBaseOps.set(tx, newComment)])

  const notifiactionsSubscriptions: SetOptional<
    NotificationSubscription,
    'id' | 'deletedAt'
  >[] = []

  notifiactionsSubscriptions.push({
    type: 'thread',
    rootId: rootComment.id,
    userIds: [newComment.authorId, ...taggedUsers],
  })

  if (comment.type === 'root' && !isDefaultBranch) {
    notifiactionsSubscriptions.push({
      type: 'branch-events',
      branchId: comment.branchId,
      userIds: [newComment.authorId, ...taggedUsers],
    })
  }

  const notificationToSend: GenericNotification = {
    read: false,
    type: 'repo-thread-comment',
    commentType: newComment.type,
    commentId: newComment.id,
    authorId: newComment.authorId.trim(),
    authorUsername,
    content: newComment.value ?? '',
    href,
    createdAt: newComment.createdAt,
    branchName: branchSlug,
    branchId: rootComment.branchId,
    repoName: repoSlug,
    rootId: rootComment.id,
  }

  return {
    comment: newComment,
    notificationSubscription: notifiactionsSubscriptions,
    notificationToSend: emitNotification ? notificationToSend : null,
  }
}

const updateComment = async (
  tx: WriteTransaction,
  { ...update }: WithServerSecret<SetRequired<Partial<Comment>, 'id'>>
): Promise<Comment> => {
  ensureBaseHubTransaction(tx)
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'editor', 'member'],
    guestAccess: 'branch-author',
  })

  const existingComment = await commentBaseOps.get(tx, update.id)
  if (!existingComment) {
    throw new Error(`Comment with id ${update.id} not found`)
  }
  let updatedComment: Comment = existingComment
  if (isRootComment(existingComment)) {
    updatedComment = {
      ...existingComment,
      ...update,
      type: 'root',
    }
  } else {
    updatedComment = {
      ...existingComment,
      ...update,
      type: 'reply',
    }
  }
  await commentBaseOps.update(tx, updatedComment)
  return updatedComment
}

const deleteComment = async (
  tx: WriteTransaction,
  { id }: WithServerSecret<{ id: string }>
) => {
  ensureBaseHubTransaction(tx)
  const commentToDelete = await commentBaseOps.get(tx, id)
  if (!commentToDelete) return

  const ops: Promise<void>[] = []

  // Update the parent because the replyId is pointing to the deleted comment
  const parent =
    commentToDelete.type === 'reply'
      ? await commentBaseOps.get(tx, commentToDelete?.parentId)
      : null
  if (parent) {
    invariant(parent, 'Parent comment not found')
    ops.push(
      commentBaseOps.update(tx, {
        ...parent,
        replyId: commentToDelete.replyId,
      })
    )
  }

  // if has children, delete them
  if (commentToDelete.type === 'reply' && commentToDelete.replyId && parent) {
    const child = await commentBaseOps.get(tx, commentToDelete.replyId)
    invariant(child, 'Child comment not found')

    ops.push(
      commentBaseOps.update(tx, {
        ...child,
        type: 'reply',
        parentId: parent.id,
      })
    )
  }

  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'editor', 'member'],
    guestAccess: 'branch-author',
  })
  await Promise.all([...ops, commentBaseOps.delete(tx, id)])
}

const resolveComment = async (
  tx: WriteTransaction,
  { id, userId }: WithServerSecret<{ id: string; userId: string }>
) => {
  ensureBaseHubTransaction(tx)
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'editor', 'member'],
    guestAccess: 'branch-author',
  })
  const existingComment = await commentBaseOps.get(tx, id)
  if (!existingComment || !isRootComment(existingComment)) {
    throw new Error(`Root comment with id ${id} not found`)
  }
  const resolvedComment: Comment = {
    ...existingComment,
    status: 'resolved',
    resolvedBy: userId,
  }
  await commentBaseOps.update(tx, resolvedComment)
}

export const openComment = async (
  tx: WriteTransaction,
  { id }: WithServerSecret<{ id: string }>
) => {
  ensureBaseHubTransaction(tx)
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'editor', 'member'],
    guestAccess: 'branch-author',
  })
  const existingComment = await commentBaseOps.get(tx, id)
  if (!existingComment || !isRootComment(existingComment)) {
    throw new Error(`Root comment with id ${id} not found`)
  }
  const openComment: Comment = {
    ...existingComment,
    status: 'open',
    resolvedBy: null,
  }
  await commentBaseOps.update(tx, openComment)
}

const addReaction = async (
  tx: WriteTransaction,
  {
    id,
    reaction,
    userId,
  }: WithServerSecret<{ id: string; reaction: string; userId: string }>
) => {
  ensureBaseHubTransaction(tx)
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'editor', 'member'],
    guestAccess: 'branch-author',
  })
  const existingComment = await commentBaseOps.get(tx, id)
  if (!existingComment) {
    throw new Error(`Comment with id ${id} not found`)
  }
  const updatedReactions = { ...existingComment.reactions }

  if (updatedReactions[reaction]?.includes(userId)) {
    console.warn(`User ${userId} already reacted with ${reaction}`)
    return
  }
  updatedReactions[reaction] = [...(updatedReactions[reaction] || []), userId]
  const updatedComment: Comment = {
    ...existingComment,
    reactions: updatedReactions,
  }
  await commentBaseOps.update(tx, updatedComment)
  return updatedComment
}

const removeReaction = async (
  tx: WriteTransaction,
  {
    id,
    reaction,
    userId,
  }: WithServerSecret<{ id: string; reaction: string; userId: string }>
) => {
  ensureBaseHubTransaction(tx)
  hasAccessOrThrow({
    serverOnly: tx.serverOnly,
    clientOnly: tx.clientOnly,
    serverSecret: tx.serverSecret,
    allowedRoles: ['owner', 'editor', 'member'],
    guestAccess: 'branch-author',
  })
  const existingComment = await commentBaseOps.get(tx, id)
  if (!existingComment) {
    throw new Error(`Comment with id ${id} not found`)
  }
  let updatedReactions = { ...existingComment.reactions }
  if (updatedReactions[reaction]) {
    updatedReactions[reaction] = updatedReactions[reaction]!.filter(
      (reactingUserId) => reactingUserId !== userId
    )
    if (updatedReactions[reaction]!.length === 0) {
      const { [reaction]: _, ...rest } = updatedReactions
      updatedReactions = rest
    }
  } else {
    throw new Error(`User ${userId} did not react with ${reaction}`)
  }
  const updatedComment: Comment = {
    ...existingComment,
    reactions: updatedReactions,
  }
  await commentBaseOps.update(tx, updatedComment)
}

// Mutators
export const commentMutators = {
  getComment: commentBaseOps.get,
  listComments: commentBaseOps.list,
  addComment,
  updateComment,
  deleteComment,
  resolveComment,
  addReaction,
  removeReaction,
  openComment,
}
